import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { RichTextEditor } from '../../../../../Components/RichTextEditor/RichTextEditor';
import { hasText, OnboardingDraftSelect, requirementNoteKey, requirementOptions, useOnboardingDraft } from './OnboardingDraft';
import { useStyles } from './Onboarding.styles';

export const OnboardingRequirementNotes = () => {
  const { draft, update, account } = useOnboardingDraft();
  const { formatMessage: t } = useIntl();
  const classes = useStyles();
  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<{ requirementId: number; unitIds: number[] }>();
  const options = requirementOptions(draft, account);
  const selectedRequirements = options.filter(requirement => (draft.requirementNoteRequirementIds || []).includes(requirement.id));
  const unitOptions = selectedRequirements.length
    ? draft.units.filter(unit => selectedRequirements.every(requirement => requirement.unitIds.includes(unit.id)))
    : draft.units;
  const optionsById = new Map(options.map(requirement => [requirement.id, requirement]));
  const savedNotesByKey = new Map<string, { id: string; requirementId: number; requirement: string; unitIds: number[]; units: string[]; note: string }>();

  Object.entries(draft.requirementNotes).forEach(([key, note]) => {
    if (!hasText(note)) return;
    const [requirementId, unitId] = key.split(':').map(Number);
    const requirement = optionsById.get(requirementId);
    const unit = draft.units.find(item => item.id === unitId);
    if (!requirement || !unit || !requirement.unitIds.includes(unitId)) return;
    const rowKey = `${requirementId}:${note}`;
    const row = savedNotesByKey.get(rowKey) || {
      id: rowKey, requirementId, requirement: requirement.name, unitIds: [], units: [], note,
    };
    if (!row.unitIds.includes(unitId)) row.unitIds.push(unitId);
    if (!row.units.includes(unit.name)) row.units.push(unit.name);
    savedNotesByKey.set(rowKey, row);
  });
  const savedNotes = Array.from(savedNotesByKey.values()).map(row => ({ ...row, unit: row.units.join(', ') }));

  const resetForm = () => {
    setEditingNote(undefined);
    setFormOpen(false);
    update(current => ({ ...current, requirementNoteRequirementIds: [], requirementNoteCompanyUnitIds: [], requirementNoteText: '' }));
  };
  const saveNote = () => {
    update(current => {
      const requirementNotes = { ...current.requirementNotes };
      const requirementNoteChanges = { ...current.requirementNoteChanges };
      editingNote?.unitIds.forEach(unitId => {
        const key = requirementNoteKey(editingNote.requirementId, unitId);
        requirementNotes[key] = '';
        requirementNoteChanges[key] = null;
      });
      (current.requirementNoteRequirementIds || []).forEach(requirementId => {
        (current.requirementNoteCompanyUnitIds || []).forEach(unitId => {
          const key = requirementNoteKey(requirementId, unitId);
          requirementNotes[key] = current.requirementNoteText || '';
          requirementNoteChanges[key] = current.requirementNoteText || null;
        });
      });
      return { ...current, requirementNotes, requirementNoteChanges,
        requirementNoteRequirementIds: [], requirementNoteCompanyUnitIds: [], requirementNoteText: '' };
    });
    setEditingNote(undefined);
    setFormOpen(false);
  };
  const editNote = (requirementId: number, unitIds: number[], note: string) => {
    setEditingNote({ requirementId, unitIds });
    setFormOpen(true);
    update(current => ({ ...current, requirementNoteRequirementIds: [requirementId], requirementNoteCompanyUnitIds: unitIds, requirementNoteText: note }));
  };
  const deleteNote = (requirementId: number, unitIds: number[]) => {
    const isEditing = editingNote?.requirementId === requirementId && editingNote.unitIds.some(id => unitIds.includes(id));
    if (isEditing) resetForm();
    update(current => {
      const requirementNotes = { ...current.requirementNotes };
      const requirementNoteChanges = { ...current.requirementNoteChanges };
      unitIds.forEach(unitId => {
        const key = requirementNoteKey(requirementId, unitId);
        requirementNotes[key] = '';
        requirementNoteChanges[key] = null;
      });
      return { ...current, requirementNotes, requirementNoteChanges };
    });
  };
  const editNoteRef = useRef(editNote);
  editNoteRef.current = editNote;
  const deleteNoteRef = useRef(deleteNote);
  deleteNoteRef.current = deleteNote;
  const actions = useMemo(() => [
    (row: { requirementId: number; unitIds: number[]; note: string }) => ({ icon: 'edit', tooltip: t({ id: 'component.mtable.editTooltip' }), onClick: () => editNoteRef.current(row.requirementId, row.unitIds, row.note) }),
    (row: { requirementId: number; unitIds: number[] }) => ({ icon: 'delete', tooltip: t({ id: 'component.mtable.deleteTooltip' }), onClick: () => deleteNoteRef.current(row.requirementId, row.unitIds) }),
  ], [t]);

  return <>
    {!formOpen && <Box className={classes.listActions}>
      <Button color="primary" variant="outlined" onClick={() => {
        update(current => ({ ...current, requirementNoteRequirementIds: [], requirementNoteCompanyUnitIds: [], requirementNoteText: '' }));
        setFormOpen(true);
      }}>{t({ id: 'onboarding.add-note' })}</Button>
    </Box>}
    {formOpen && <Grid container spacing={2}>
      <Grid item xs={12} sm={6}><OnboardingDraftSelect name="requirement-note-requirements" label={t({ id: 'onboarding.select-requirement-notes' })} ids={draft.requirementNoteRequirementIds || []} options={options} onChange={requirementNoteRequirementIds => update(current => {
        const selected = options.filter(requirement => requirementNoteRequirementIds.includes(requirement.id));
        const validUnits = selected.length ? current.units.filter(unit => selected.every(requirement => requirement.unitIds.includes(unit.id))) : current.units;
        const validUnitIds = new Set(validUnits.map(unit => unit.id));
        return { ...current, requirementNoteRequirementIds, requirementNoteCompanyUnitIds: (current.requirementNoteCompanyUnitIds || []).filter(id => validUnitIds.has(id)) };
      })} /></Grid>
      <Grid item xs={12} sm={6}><OnboardingDraftSelect name="requirement-note-company-units" label={t({ id: 'form.selectCompanyUnits' })} ids={draft.requirementNoteCompanyUnitIds || []} options={unitOptions} onChange={requirementNoteCompanyUnitIds => update(current => ({ ...current, requirementNoteCompanyUnitIds }))} /></Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2">{t({ id: 'law-portal.overview-notes.form.note.label' })}</Typography>
        <RichTextEditor value={draft.requirementNoteText || ''} onChange={requirementNoteText => update(current => ({ ...current, requirementNoteText }))} overrideConfig={{ height: 140 }} />
      </Grid>
      <Grid item xs={12} className={classes.formActions}>
        <Button color="primary" variant="outlined" disabled={!(draft.requirementNoteRequirementIds || []).length || !(draft.requirementNoteCompanyUnitIds || []).length || !hasText(draft.requirementNoteText || '')} onClick={saveNote}>{t({ id: editingNote ? 'form.save' : 'form.add' })}</Button>
        <Button style={{ marginLeft: 8 }} onClick={resetForm}>{t({ id: 'form.cancel' })}</Button>
      </Grid>
    </Grid>}
    <MTable data={savedNotes} columns={[
      { title: t({ id: 'onboarding.requirement-name' }), field: 'requirement' },
      { title: t({ id: 'law-portal.overview-notes.form.company-units.label' }), field: 'unit' },
      { title: t({ id: 'law-portal.manage.requirements.table.notes' }), field: 'note', render: row => <HtmlView value={row.note} /> },
    ]} actions={actions} />
  </>;
};
